import { FAST_API_URL, postOptions } from "../index";

// Cut-videos clip editing. When the /fast editor opens a clip (URL has ?clip={clip_id}), the clip is
// materialized on disk (subs + audio downloaded from the CDN) so the normal editor endpoints work; after
// the translator edits a line, `republish` rebuilds clip_subs.vtt from the clip's OWN edited chunks and
// pushes it to R2 (via site_server), then deletes the on-disk folder. See translate-python clip_edit.py.

export interface MaterializeClipResult {
    ok: boolean;
    clip_id: string;
    materialized: boolean;
    cached: boolean;
    n_cues: number;
    n_segments?: number;
}

export interface RepublishClipResult {
    ok: boolean;
    clip_id: string;
    n_cues: number;
    republish: { ok: boolean; hls: string; version: number };
    folder_removed: boolean;
}

// `lang` selects which language's chunks to materialize/republish (ru = source; en/de/es/zh/ar = the
// translation being edited). Without it the backend defaults to ru, so a translator's EN edit would
// never get published. Backend codes: ru, en, de, es, zh, ar, kz (map the editor's `ch` -> `zh`).
export const materializeClipApi = async (
    clipId: string,
    lang = "ru",
    source = "ru",
    force = false,
): Promise<MaterializeClipResult> => {
    const url = `${FAST_API_URL}/materialize_clip?clip_id=${encodeURIComponent(
        clipId,
    )}&lang=${encodeURIComponent(lang)}&source=${encodeURIComponent(source)}${
        force ? "&force=true" : ""
    }`;
    const response = await fetch(url, { ...postOptions, body: "{}" });
    if (!response.ok) {
        throw new Error(`materialize_clip failed: ${response.status}`);
    }
    return await response.json();
};

export const republishClipApi = async (
    clipId: string,
    lang = "ru",
    source = "ru",
): Promise<RepublishClipResult> => {
    const url = `${FAST_API_URL}/republish_clip?clip_id=${encodeURIComponent(
        clipId,
    )}&lang=${encodeURIComponent(lang)}&source=${encodeURIComponent(source)}`;
    const response = await fetch(url, { ...postOptions, body: "{}" });
    if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `republish_clip failed: ${response.status}`);
    }
    return await response.json();
};
